import React, { useEffect, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCurrentEmployee } from '../../services/pinAuthService';
import { getEmployeeBalance } from '../../services/simpleCompService';
import { generateQRData } from '../../services/simpleEmployeeService';
import QRCode from 'qrcode.react';
import styles from './QRCodeDisplay.module.css';

const QR_REFRESH_MS = 30 * 1000;

export const QRCodeDisplay: React.FC = () => {
  const employee = getCurrentEmployee();
  const [qrValue, setQrValue] = useState<string>('');
  const [refreshCount, setRefreshCount] = useState(0);

  const { data: balanceResult, isLoading } = useQuery({
    queryKey: ['employee-balance', employee?.id, refreshCount],
    queryFn: () => employee?.id
      ? getEmployeeBalance(employee.id)
      : Promise.resolve({ data: { drinks_available: 0, food_available: 0 }, error: null }),
    refetchInterval: QR_REFRESH_MS,
  });

  const generateQRPayload = useCallback(() => {
    if (!employee?.id) return '';
    return generateQRData(employee.id);
  }, [employee?.id]);

  useEffect(() => {
    setQrValue(generateQRPayload());
  }, [generateQRPayload]);

  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshCount((prev) => prev + 1);
      setQrValue(generateQRPayload());
    }, QR_REFRESH_MS);

    return () => clearInterval(interval);
  }, [generateQRPayload]);

  const handleRefresh = () => {
    setRefreshCount((prev) => prev + 1);
    setQrValue(generateQRPayload());
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        LOADING QR CODE...
      </div>
    );
  }

  const balance = balanceResult?.data || { drinks_available: 0, food_available: 0 };

  return (
    <div className={styles.qrDisplay}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1>YOUR QR CODE</h1>
          <p>Scan this at the register to redeem your comps</p>
        </div>

        <div className={styles.refreshBar}>
          <button onClick={handleRefresh} className={styles.refreshButton}>
            REFRESH ↻
          </button>
          <div className={styles.refreshTime}>
            Auto-refresh every 30 seconds
          </div>
        </div>

        <div className={styles.qrWrapper}>
          <div className={styles.qrContainer}>
            <QRCode
              value={qrValue}
              size={300}
              level="H"
              includeMargin={true}
              className={styles.qrCode}
              renderAs="svg"
            />
          </div>

          <div className={styles.userInfo}>
            {employee?.photo_url ? (
              <img src={employee.photo_url} alt={employee.name} className={styles.userPhoto} />
            ) : (
              <div className={styles.userAvatar}>
                {employee?.name?.charAt(0).toUpperCase()}
              </div>
            )}
            <div className={styles.userName}>
              {employee?.name}
            </div>
          </div>
        </div>

        <div className={styles.balanceInfo}>
          <h2>CURRENT COMPS</h2>
          <div className={styles.balanceGrid}>
            <div className={styles.balanceCard}>
              <div className={styles.balanceIcon}>🍹</div>
              <div className={styles.balanceValue}>{balance.drinks_available}</div>
              <div className={styles.balanceLabel}>DRINKS</div>
            </div>
            <div className={styles.balanceCard}>
              <div className={styles.balanceIcon}>🍕</div>
              <div className={styles.balanceValue}>{balance.food_available}</div>
              <div className={styles.balanceLabel}>FOOD</div>
            </div>
          </div>
        </div>

        <div className={styles.instructions}>
          <h2>HOW TO USE</h2>
          <ul>
            <li>1. Show this QR code to the staff at the register</li>
            <li>2. Staff will scan your code to verify your identity</li>
            <li>3. Select the type of comp you want to redeem (food or drink)</li>
            <li>4. Confirm the redemption</li>
            <li>5. Your balance will be updated automatically</li>
          </ul>
        </div>

        <div className={styles.warning}>
          <strong>NOTE:</strong> Keep your QR code secure and only show it to authorized staff.
        </div>
      </div>
    </div>
  );
};
